import type { SVGProps } from 'react';

import { usePublicConfig } from '@/features/settings/hooks/useServicesSettings';
import type { SocialPlatform } from '@/features/settings/services/settings.service';

type IconProps = SVGProps<SVGSVGElement>;

function InstagramIcon(props: IconProps) {
  const gradientId = 'social-instagram-gradient';
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <defs>
        <radialGradient id={gradientId} cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#FFDD55" />
          <stop offset="10%" stopColor="#FFDD55" />
          <stop offset="50%" stopColor="#FF543E" />
          <stop offset="100%" stopColor="#C837AB" />
        </radialGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="6" fill={`url(#${gradientId})`} />
      <circle cx="12" cy="12" r="4.4" stroke="#fff" strokeWidth="1.8" />
      <circle cx="17.1" cy="6.9" r="1.1" fill="#fff" />
    </svg>
  );
}

function FacebookIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="10" fill="#1877F2" />
      <path
        d="M13.5 21v-7.2h2.4l.4-2.8h-2.8v-1.8c0-.8.2-1.3 1.4-1.3h1.5V5.3c-.3 0-1.1-.1-2.1-.1-2.1 0-3.5 1.3-3.5 3.6v2h-2.4v2.8h2.4V21h2.7z"
        fill="#fff"
      />
    </svg>
  );
}

function TikTokIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="10" fill="#000" />
      <path
        d="M15.65 6.5c.4 1.6 1.5 2.8 3 3.2v2.4c-1.1 0-2.1-.3-3-.9v4.5a4.3 4.3 0 1 1-4.3-4.3c.2 0 .4 0 .6.1v2.4a1.9 1.9 0 1 0 1.3 1.8V6.5h2.4z"
        fill="#fff"
      />
    </svg>
  );
}

function YouTubeIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <rect x="1" y="4" width="22" height="16" rx="5" fill="#FF0000" />
      <path d="M10 8.3l6.5 3.7-6.5 3.7v-7.4z" fill="#fff" />
    </svg>
  );
}

function XIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="10" fill="#000" />
      <path d="M7 7l10 10M17 7L7 17" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function TelegramIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="10" fill="#26A5E4" />
      <path d="M7 12.5l10-5-3.2 10-2.3-3.8-2.8 2.3.3-3.5z" fill="#fff" />
    </svg>
  );
}

function WhatsAppIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="10" fill="#25D366" />
      <path
        d="M8.5 8.7c.2-.5.4-.5.6-.5h.5c.2 0 .4 0 .5.4.2.5.6 1.5.6 1.6.1.1.1.3 0 .4-.1.2-.2.3-.3.4-.1.1-.3.3-.4.4-.1.1-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.2 1 2.1 1.4 2.4 1.5.3.1.5.1.6-.1.2-.2.7-.8.9-1.1.2-.3.4-.2.6-.1l1.8.9c.2.1.3.2.4.3.1.2.1.9-.2 1.4-.3.5-1.4 1.1-2.4 1.1-1 0-2.5-.4-4.6-2.2-2.5-2.2-3.3-4.2-3.4-4.5-.1-.3-.7-1.2-.7-2.3 0-1.1.6-1.6.8-1.8z"
        fill="#fff"
      />
    </svg>
  );
}

export const SOCIAL_PLATFORM_ICONS: Record<SocialPlatform, (props: IconProps) => JSX.Element> = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
  x: XIcon,
  telegram: TelegramIcon,
  whatsapp: WhatsAppIcon,
};

export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
};

interface SocialLinksProps {
  className?: string;
}

/**
 * Icone social configurabili dal super-admin (pannello Impostazioni > Social).
 * Nasconde del tutto se non ci sono piattaforme visibili — non renderizza
 * un footer vuoto/placeholder.
 */
export function SocialLinks({ className }: SocialLinksProps) {
  const { data } = usePublicConfig();
  const social = data?.social ?? [];

  if (social.length === 0) return null;

  return (
    <div className={className ?? 'flex items-center justify-center gap-4'}>
      {social.map(({ platform, url }) => {
        const Icon = SOCIAL_PLATFORM_ICONS[platform];
        if (!Icon) return null;
        const label = SOCIAL_PLATFORM_LABELS[platform];
        return (
          <a
            key={platform}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="opacity-90 transition-opacity hover:opacity-100"
          >
            <Icon className="h-6 w-6" />
          </a>
        );
      })}
    </div>
  );
}
